import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geoBlocked, setGeoBlocked] = useState(null); // { country, blocked } | null
  const { setAuth } = useAuth();
  const navigate = useNavigate();

  // Pull referral code from URL (?ref=…)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferralCode(ref.toUpperCase());
  }, []);

  // Geo block check — run once on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/legal/geo-check`);
        setGeoBlocked(res.data);
      } catch {
        // ignore; not blocking signup if check fails
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (geoBlocked?.blocked) {
      toast.error('Signups are not available in your region.');
      return;
    }
    if (!acceptedLegal) {
      toast.error('You must accept the Terms and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    try {
      // 1. Register via raw axios so we don't flip global auth state yet
      //    (that would mount LegalAcceptanceModal and race the /accept call).
      const payload = { email, password, name };
      if (referralCode) payload.referral_code_used = referralCode;
      const regRes = await axios.post(`${API_URL}/api/auth/register`, payload);
      const { token: newToken, user: userData } = regRes.data;
      const authHeader = { Authorization: `Bearer ${newToken}` };

      // 2. Record acceptance of every published legal doc BEFORE we flip
      //    AuthContext, so LegalAcceptanceModal's first fetch returns [].
      try {
        const pending = await axios.get(`${API_URL}/api/legal/pending-acceptance`, { headers: authHeader });
        const items = (pending.data.pending || []).map(p => ({ slug: p.slug, version: p.version }));
        if (items.length) {
          await axios.post(
            `${API_URL}/api/legal/accept`,
            { acceptances: items },
            { headers: authHeader }
          );
        }
      } catch {
        /* non-fatal — modal will prompt on next load */
      }

      // 3. Now commit the session globally.
      setAuth(newToken, userData);
      toast.success('Account created successfully!');
      navigate('/memberships');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const blocked = geoBlocked?.blocked;

  return (
    <div className="min-h-screen bg-background luxury-mesh-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <img src="/wemillionaires-logo.png" alt="We Masterminds" className="h-16 w-auto" />
            <h1 className="text-3xl font-playfair font-bold text-foreground tracking-wide">WE MASTERMINDS</h1>
          </Link>
          <p className="text-muted-foreground mt-2">Create your account</p>
        </div>

        {blocked && (
          <div
            className="mb-6 p-4 border border-red-500/50 bg-red-500/10 rounded-sm flex items-start gap-3"
            data-testid="geo-blocked-banner"
          >
            <ShieldAlert className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-red-400">Signups unavailable in your region.</p>
              <p className="text-muted-foreground text-xs mt-1">
                Based on your detected location ({geoBlocked.country}), WeMasterminds is not currently
                offered in your jurisdiction. See our{' '}
                <Link to="/legal/terms-of-service" className="text-gold hover:underline">Terms of Service</Link> for details.
              </p>
            </div>
          </div>
        )}

        <div className="wm-card p-8" data-testid="register-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-foreground">Full Name</Label>
              <Input
                id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                required className="mt-1 bg-input border-border text-foreground"
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required className="mt-1 bg-input border-border text-foreground"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={8}
                className="mt-1 bg-input border-border text-foreground"
                data-testid="password-input"
              />
            </div>

            <div>
              <Label htmlFor="referral" className="text-foreground">
                Referral Code <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Input
                id="referral" type="text" value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Enter referral code if you were recruited"
                className="mt-1 bg-input border-border text-foreground font-mono"
                data-testid="referral-code-input"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="accept-legal-wrapper">
              <input
                type="checkbox"
                checked={acceptedLegal}
                onChange={(e) => setAcceptedLegal(e.target.checked)}
                className="mt-1 w-4 h-4 accent-gold cursor-pointer"
                data-testid="accept-legal-checkbox"
              />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I am 18 or older and I have read and agree to the{' '}
                <Link to="/legal/terms-of-service" target="_blank" className="text-gold hover:underline">Terms of Service</Link>,{' '}
                <Link to="/legal/privacy-policy" target="_blank" className="text-gold hover:underline">Privacy Policy</Link>,{' '}
                <Link to="/legal/earnings-disclaimer" target="_blank" className="text-gold hover:underline">Earnings Disclaimer</Link>,{' '}
                and{' '}
                <Link to="/legal/member-agreement" target="_blank" className="text-gold hover:underline">Member Agreement</Link>.
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading || blocked || !acceptedLegal}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
